import React from 'react';
import IFlightRangeData from '../../types/FlightRanges';
import FlightRangeModifyTable from '../FlightRange/FlightRangeModifyTable';
import './ConfirmationModal.scss';
import IReplacementRange from '../../types/ReplacementRange';
import { EnvConfigs } from '../../http-common';
import { NotificationData } from '../../types/NotificationData';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  onEditNotification?: () => void;
  selectedRanges: IFlightRangeData[];
  replacementRanges: IReplacementRange[];
  notificationData?: NotificationData | null;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onEditNotification,
  selectedRanges,
  replacementRanges,
  notificationData
}) => {
  if (!isOpen) return null;

  const enableNotificationWorkflow = EnvConfigs.enableNotificationWorkflow;
  const showEditNotification = enableNotificationWorkflow && onEditNotification;

  return (
    <div className="confirmation-modal-overlay" role="presentation">
      <div
        className="confirmation-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirmation-modal-title"
      >
        <div className="confirmation-modal__header">
          <h2 id="confirmation-modal-title" className="confirmation-modal__title">
            Confirm Changes
          </h2>
          <button
            className="confirmation-modal__close-button"
            onClick={onClose}
            aria-label="Close confirmation modal"
          >
            ×
          </button>
        </div>
        
        <div className="confirmation-modal__content">
          <p>Please review the changes below before proceeding:</p>
          
          {enableNotificationWorkflow && notificationData && (
            <div className="confirmation-modal__notification-preview">
              <h3 className="confirmation-modal__notification-preview-title">Notification Email Preview</h3>
              <div className="confirmation-modal__notification-preview-content">
                <div className="confirmation-modal__notification-field">
                  <strong>From:</strong> <span>{notificationData.from}</span>
                </div>
                <div className="confirmation-modal__notification-field">
                  <strong>To:</strong> <span>{notificationData.to}</span>
                </div>
                <div className="confirmation-modal__notification-field">
                  <strong>CC:</strong> <span>{notificationData.cc || '(none)'}</span>
                </div>
                <div className="confirmation-modal__notification-field">
                  <strong>Subject:</strong> <span>{notificationData.subject}</span>
                </div>
                <div className="confirmation-modal__notification-field confirmation-modal__notification-body">
                  <strong>Message:</strong>
                  <div 
                    className="confirmation-modal__notification-html" 
                    dangerouslySetInnerHTML={{ __html: notificationData.userText }}
                  />
                </div>
              </div>
            </div>
          )}
          
          <div className="confirmation-modal__tables">
            <div className="confirmation-modal__table-section">
              <FlightRangeModifyTable
                title="Discontinued Flight(s)"
                ranges={selectedRanges}
                showRemoveButton={false}
              />
            </div>
            
            <div className="confirmation-modal__table-section">
              <FlightRangeModifyTable
                title="Replacement Flight(s)"
                ranges={replacementRanges}
                showRemoveButton={false}
              />
            </div>
          </div>
          
          <div className="confirmation-modal__message">
            <p>Are you sure you want to proceed with these modifications?</p>
          </div>
        </div>
        
        <div className="confirmation-modal__footer">
          <div className="confirmation-modal__footer-container">
            <div className="confirmation-modal__footer-left">
              <auro-button
                className="confirmation-modal__go-back-button"
                onClick={onClose}
                aria-label="Go back to flight ranges modification"
                variant='secondary'
                size='md'
              >
                Go back
              </auro-button>
              <auro-button
                className="confirmation-modal__save-draft-button"
                onClick={onClose}
                aria-label="Save changes as draft"
                variant='tertiary'
                size='md'
              >
                Save as draft
              </auro-button>
            </div>
            <div className="confirmation-modal__footer-right">
              {showEditNotification && (
                <auro-button
                  className="confirmation-modal__edit-notification-button"
                  onClick={onEditNotification}
                  aria-label="Edit notification"
                  title="Customize notification email before confirming"
                  variant="secondary"
                  size="md"
                  responsive
                  fluid
                  style={
                      {
                      minWidth: '212px',
                      height: '50px',
                      marginRight: '16px'
                      } as React.CSSProperties
                  }
                >
                  Edit Notification
                </auro-button>
              )}
              <auro-button
                className="confirmation-modal__confirm-button"
                onClick={onConfirm}
                aria-label={enableNotificationWorkflow ? "Confirm and notify" : "Confirm"}
                title={enableNotificationWorkflow ? "Confirm changes and send notification email" : "Confirm changes"}
                variant="primary"
                size="md"
                responsive
                fluid
                style={
                    {
                    '--ds-auro-button-container-image': 'var(--color-primary, #0074ca)',
                    minWidth: '212px',
                    height: '50px'
                    } as React.CSSProperties
                }
              >
                {enableNotificationWorkflow ? 'Confirm and Notify' : 'Confirm'}
              </auro-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
